import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { listJobs } from '@/api/jobs';
import { useAuthStore } from '@/stores/authStore';
import { dueCountdown } from '@/utils/due';

import styles from './deadline-reminder.module.css';

/**
 * 学生端：截止前 24 小时右下角弹窗提醒。
 * 仅对已发布、有截止时间、未提交（或被打回）且距截止不足 24h 的作业弹窗。
 */
export function DeadlineReminder() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const { data } = useQuery({
    queryKey: ['jobs', 'deadline-reminder'],
    queryFn: () => listJobs(),
    refetchInterval: 60_000,
    enabled: user?.role === 'student',
  });

  // 每分钟刷新一次"距截止还剩多久"，触发重渲染
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  const urgentJobs = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((job) => {
      if (job.status !== 'published') return false;
      const cd = dueCountdown(job.due_date);
      if (cd.level !== 'urgent') return false; // 仅 24h 以内
      // 未提交 或 被打回需要重交
      const notSubmitted = !job.my_submission_status;
      const rejected = job.review_status === 'rejected';
      if (!notSubmitted && !rejected) return false;
      return true;
    });
  }, [data]);

  const visible = urgentJobs.filter((j) => !dismissed.has(j.id));

  if (visible.length === 0) return null;

  const dismiss = (id: number) => {
    setDismissed((prev) => new Set(prev).add(id));
  };

  return (
    <div className={styles.container}>
      {visible.map((job) => {
        const cd = dueCountdown(job.due_date);
        return (
          <div key={job.id} className={styles.popup} role="alert">
            <div className={styles.icon}>⏰</div>
            <div className={styles.body}>
              <div className={styles.title}>{job.title}</div>
              <div className={styles.desc}>
                距截止仅剩 <b>{cd.text}</b>，请尽快提交！
              </div>
              <div className={styles.btnRow}>
                <button
                  type="button"
                  className={styles.goBtn}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  去提交
                </button>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={() => dismiss(job.id)}
                >
                  稍后提醒
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
